import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AppHeader } from "../../components/AppHeader";
import { Loading } from "../../components/Loading";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, LogOut, KeyRound, UserX, UserCheck, ArrowLeft,
  AlertTriangle, ShieldOff, Mail, Phone, Users,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const INPUT = "w-full h-12 px-4 rounded-xl border border-slate-300 focus:border-[#C86D51] outline-none text-base bg-white";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-]{6,19}$/;

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function ManagerForm({ initial, onSave, onCancel, submitting }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [password, setPassword] = useState("");
  const submit = () => {
    const n = name.trim();
    if (!n) { toast.error("Please enter the manager's name."); return; }
    const e = email.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) { toast.error("Please enter a valid email address."); return; }
    const p = phone.trim();
    if (!PHONE_RE.test(p)) { toast.error("Please enter a valid phone number (7-20 digits, may start with +)."); return; }
    if (!isEdit) {
      if (!password || password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    } else if (password && password.length < 6) {
      toast.error("New password must be at least 6 characters."); return;
    }
    const payload = { name: n, email: e, phone: p };
    if (password) payload.password = password;
    onSave(payload);
  };
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Manager Name</label>
        <input data-testid="manager-form-name" className={INPUT} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Email</label>
        <input data-testid="manager-form-email" type="email" autoComplete="off" className={INPUT} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Phone</label>
        <input data-testid="manager-form-phone" className={INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98XXX 12345" />
      </div>
      <div>
        <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">
          {isEdit ? "New Password (leave blank to keep)" : "Password"}
        </label>
        <input data-testid="manager-form-password" type="password" autoComplete="new-password" className={INPUT} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 characters" />
      </div>
      <DialogFooter className="gap-2">
        <button onClick={onCancel} className="flex-1 py-3 rounded-xl border border-slate-200 font-bold text-slate-700">Cancel</button>
        <button data-testid="manager-form-save" onClick={submit} disabled={submitting}
          className="flex-1 py-3 rounded-xl copper-gradient text-white font-bold shadow-md active:scale-[0.98] transition-transform disabled:opacity-60">
          {submitting ? "Saving…" : (isEdit ? "Save Changes" : "Create Manager")}
        </button>
      </DialogFooter>
    </div>
  );
}

export default function ManagerManagement() {
  const navigate = useNavigate();
  const [managers, setManagers] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOf, setEditOf] = useState(null);
  const [deleteOf, setDeleteOf] = useState(null);
  const [logoutOf, setLogoutOf] = useState(null);
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/managers/all").then((r) => setManagers(r.data)).catch(() => setManagers([]));
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    setSubmitting(true);
    try {
      await api.post("/managers", payload);
      toast.success("Manager created.");
      setAddOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const edit = async (payload) => {
    setSubmitting(true);
    try {
      await api.put(`/managers/${editOf.id}`, payload);
      toast.success("Manager updated.");
      setEditOf(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const doDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/managers/${deleteOf.id}`);
      toast.success(`Deleted ${deleteOf.name}. Their active sessions are invalidated.`);
      setDeleteOf(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const doForceLogout = async () => {
    setSubmitting(true);
    try {
      await api.post(`/managers/${logoutOf.id}/force-logout`);
      toast.success(`${logoutOf.name} signed out from all devices.`);
      setLogoutOf(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const doLogoutAll = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/managers/logout-all`);
      toast.success(`All managers signed out (${data.count}).`);
      setLogoutAllOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      <AppHeader title="Manager Management" subtitle="Owner" />
      <div className="px-4 lg:px-8 py-4 space-y-5 lg:max-w-3xl lg:mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-semibold text-slate-500">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-[#F7E7DE] flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-[#9A462B]" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display text-base font-bold text-slate-900">Managers</h2>
                <p className="text-xs text-slate-500 mt-0.5">Create and manage factory manager accounts</p>
              </div>
            </div>
            <button data-testid="add-manager-button" onClick={() => setAddOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl copper-gradient text-white font-bold text-sm shadow-sm active:scale-[0.98] transition-transform">
              <Plus className="h-4 w-4" /> Add Manager
            </button>
          </div>

          {managers === null ? <Loading /> : managers.length === 0 ? (
            <p className="text-sm text-slate-400">No managers yet. Tap "Add Manager" to create the first one.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="managers-list">
              {managers.map((m) => (
                <li key={m.id} data-testid={`manager-row-${m.id}`} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-800 truncate">{m.name}</p>
                      <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">Active</span>
                    </div>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5 truncate"><Mail className="h-3 w-3 flex-shrink-0" />{m.email}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3 flex-shrink-0" />{m.phone || "—"}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{m.jobs_count} job{m.jobs_count === 1 ? "" : "s"} · joined {fmtTime(m.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <button data-testid={`manager-edit-${m.id}`} onClick={() => setEditOf(m)} title="Edit"
                      className="p-2 rounded-lg bg-slate-50 text-slate-600 active:scale-95 transition-transform"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`manager-force-logout-${m.id}`} onClick={() => setLogoutOf(m)} title="Force Logout"
                      className="p-2 rounded-lg bg-amber-50 text-amber-700 active:scale-95 transition-transform"><LogOut className="h-4 w-4" /></button>
                    <button data-testid={`manager-delete-${m.id}`} onClick={() => setDeleteOf(m)} title="Delete"
                      className="p-2 rounded-lg bg-red-50 text-red-600 active:scale-95 transition-transform"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-red-200 p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
              <ShieldOff className="h-5 w-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-display text-base font-bold text-slate-900">Logout All Managers</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Immediately invalidate every active manager session on every device and browser,
                including persistent 30-day logins. Your Owner session is not affected.
              </p>
            </div>
          </div>
          <button data-testid="logout-all-managers-button" onClick={() => setLogoutAllOpen(true)}
            className="w-full py-3 rounded-xl bg-red-600 text-white font-bold shadow-sm active:scale-[0.98] transition-transform flex items-center justify-center gap-2">
            <LogOut className="h-5 w-5" /> Logout All Managers
          </button>
        </section>
      </div>

      {/* Add Manager */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Add Manager</DialogTitle>
            <DialogDescription>Create a new manager account with login credentials.</DialogDescription>
          </DialogHeader>
          <ManagerForm onSave={create} onCancel={() => setAddOpen(false)} submitting={submitting} />
        </DialogContent>
      </Dialog>

      {/* Edit Manager */}
      <Dialog open={!!editOf} onOpenChange={(v) => !v && setEditOf(null)}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Edit Manager</DialogTitle>
            <DialogDescription>Update details, reset password, or deactivate.</DialogDescription>
          </DialogHeader>
          {editOf && <ManagerForm initial={editOf} onSave={edit} onCancel={() => setEditOf(null)} submitting={submitting} />}
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteOf} onOpenChange={(v) => !v && setDeleteOf(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-manager-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Permanently delete this manager?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                <p>
                  <b>{deleteOf?.name}</b> &lt;{deleteOf?.email}&gt; will be permanently removed.
                  Their active sessions on every device and browser are invalidated
                  immediately — including persistent 30-day logins and saved PINs.
                  They can no longer log in with this email and password.
                </p>
                <p>
                  Jobs, ratings, reports, and audit history for this manager are kept intact.
                </p>
                <p className="font-bold text-red-600">This action cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Manager</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-manager" onClick={doDelete} disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white">
              Yes, Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Force Logout one */}
      <AlertDialog open={!!logoutOf} onOpenChange={(v) => !v && setLogoutOf(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="force-logout-manager-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <LogOut className="h-5 w-5" /> Sign out this manager?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <b>{logoutOf?.name}</b> will be signed out from every device and will need to log in again. The account is kept active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-force-logout-manager" onClick={doForceLogout} disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white">
              Yes, Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Logout All */}
      <AlertDialog open={logoutAllOpen} onOpenChange={setLogoutAllOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="logout-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Logout ALL managers?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Every manager will be signed out from every device and browser immediately — including persistent 30-day logins. Your Owner session will stay signed in. Managers will need to log in with their password again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-logout-all" onClick={doLogoutAll} disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white">
              Yes, Logout All Managers
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
